import { VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components/native";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import { css } from "styled-components/native";

type TooltipButtonProps = {
  children: React.ReactNode;
  tooltip?: string;
  enableTooltip: boolean;
  renderComponent?: React.ReactNode;
  width?: string;
  position?: "top" | "bottom";
  customZIndex?: number;
  backgroundColor?: string;
  textColor?: string;
  alignSelfFlexStart?: boolean;
};

const TooltipButton = ({
  children,
  tooltip,
  enableTooltip,
  renderComponent,
  width = "240px",
  position = "top",
  customZIndex,
  backgroundColor,
  textColor,
  alignSelfFlexStart = false,
}: TooltipButtonProps) => {
  const [hovered, setHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [ready, setReady] = useState(false);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const { typography, theme } = useScreenOptionsHooks();

  const handleMouseEnter = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top:
        position === "top"
          ? rect.top + window.scrollY - 10
          : rect.bottom + window.scrollY + 10,
      left: rect.left + rect.width / 2,
    });
    setHovered(true);
  };

  const handleMouseLeave = () => {
    setHovered(false);
  };

  useEffect(() => {
    if (!hovered) {
      setReady(false);
      return;
    }
    let frame = 0;
    const measure = () => {
      const node = tooltipRef.current as HTMLDivElement | null;
      if (node?.getBoundingClientRect) {
        const rect = node.getBoundingClientRect();
        setOffset({ x: 0, y: position === "top" ? -rect.height + 10 : 0 });
        setReady(true);
        return;
      }
      frame = requestAnimationFrame(measure);
    };
    frame = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(frame);
  }, [hovered, position, tooltip]);

  return (
    <ToolTipView
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      alignSelfFlexStart={alignSelfFlexStart}
    >
      {children}
      {hovered &&
        enableTooltip &&
        ReactDOM.createPortal(
          <TooltipContainer
            ref={tooltipRef}
            style={{
              position: "absolute",
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: `translateX(-50%) translateY(${offset.y}px)`,
              opacity: ready ? 1 : 0,
            }}
            customZIndex={customZIndex}
          >
            {position === "bottom" && (
              <TooltipArrow
                position="bottom"
                customBackgroundColor={backgroundColor}
              />
            )}
            <Tooltip width={width} customBackgroundColor={backgroundColor}>
              {tooltip && (
                <VDBText
                  style={typography["Minor-R"]}
                  color={textColor || theme["core-01"]}
                >
                  {tooltip}
                </VDBText>
              )}
              {renderComponent && renderComponent}
            </Tooltip>
            {position === "top" && (
              <TooltipArrow
                position="top"
                customBackgroundColor={backgroundColor}
              />
            )}
          </TooltipContainer>,
          document.body
        )}
    </ToolTipView>
  );
};

const ToolTipView = styled.View<{ alignSelfFlexStart?: boolean }>`
  ${({ alignSelfFlexStart }) => alignSelfFlexStart && "align-self: flex-start;"}
`;

const TooltipContainer = styled.View<{ customZIndex?: number }>`
  align-items: center;
  z-index: ${({ customZIndex }) => customZIndex || 1000};
`;

const Tooltip = styled.View<{ width: string; customBackgroundColor?: string }>`
  background-color: ${({ theme, customBackgroundColor }) =>
    customBackgroundColor || theme["core-06"]};
  padding: 8px;
  width: ${({ width }) => width};
  border-radius: ${SpaceType.Basic}px;
  shadow-color: #000;
  shadow-offset: 0px 2px;
  shadow-opacity: 0.15;
  shadow-radius: 8px;
  elevation: 4;
`;

const TooltipArrow = styled.View<{
  position: "top" | "bottom";
  customBackgroundColor?: string;
}>`
  width: 0px;
  height: 0px;
  border-left-width: 8px;
  border-right-width: 8px;
  border-left-color: transparent;
  border-right-color: transparent;
  ${({ position, theme, customBackgroundColor }) =>
    position === "top"
      ? css`
          border-top-width: 8px;
          border-top-color: ${customBackgroundColor || theme["core-06"]};
          margin-bottom: 5px;
        `
      : css`
          border-bottom-width: 8px;
          border-bottom-color: ${customBackgroundColor || theme["core-06"]};
          margin-top: -5px;
        `}
`;
export default TooltipButton;
